import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import {
  Plus, Minus, Save, X, BarChart3, CheckCircle,
  Loader2, Calendar, CalendarDays, Clock, RefreshCw, TrendingUp, Users,
  ArrowDown, ArrowUp, TrendingDown, Minus as MinusIcon, Sparkles, Lightbulb,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { backendUrl } from './gameplanUtils';
import { useAuth } from '../../contexts/AuthContext';

// --- Metric definitions ───────────────────────────────────────────────────
// Each metric belongs to one of two roles:
//   • manual    — broker keys this into Quick Entry (Portal Invites / Portal
//                 Completions / Proposals). Stored via POST /api/gameplan/metrics.
//   • derived   — read-only; the count is computed from the Board / Lodged
//                 Deals / Dashboard tabs so there's a single source of truth
//                 and the broker never double-keys a number.
const METRICS = [
  {
    key: 'portal_invites',     label: 'Portal Invites',     short: 'Invites',
    icon: Plus,           color: 'bg-indigo-500', manual: true,
  },
  {
    key: 'portal_completions', label: 'Portal Completions', short: 'Completions',
    icon: CheckCircle,    color: 'bg-cyan-500',   manual: true,
  },
  {
    key: 'proposals',          label: 'Proposals',          short: 'Proposals',
    icon: BarChart3,      color: 'bg-purple-500', manual: true,
  },
  {
    key: 'lodgments',          label: 'Lodgments',          short: 'Lodgments',
    icon: BarChart3,      color: 'bg-amber-500',  manual: false, derivedStage: 'submitted',
  },
  {
    key: 'approvals',          label: 'Approvals',          short: 'Approvals',
    icon: BarChart3,      color: 'bg-emerald-500',manual: false, derivedStage: 'approved',
  },
  {
    key: 'settlements',        label: 'Settlements',        short: 'Settlements',
    icon: TrendingUp,     color: 'bg-green-600',  manual: false, derivedStage: 'settled',
  },
];

// Quick-Entry only includes metrics where `manual: true` (the 3 fields above).
// Lodgments / Approvals / Settlements live on the Board / Lodged Deals /
// Dashboard tabs already, so manual entry would duplicate the source of truth.
const MANUAL_ENTRY_METRICS = METRICS.filter(m => m.manual);

const DEFAULT_METRICS_BROKER = {
  id: 'f34e765f-fa2c-4fba-88fa-87e661223771',
  name: 'Emmanuel',
};

// Period helpers — Tasmania-local "today" for now/today, then naïve UTC week/month
// boundaries. Good enough for the demo; matches the existing /api/activity backend.
const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};
const startOfWeek = () => {
  const d = startOfToday();
  const day = d.getDay() || 7; // Sunday=0 → 7 so week starts Monday
  d.setDate(d.getDate() - (day - 1));
  return d;
};
const startOfMonth = () => {
  const d = startOfToday();
  d.setDate(1);
  return d;
};
const startOfPrevMonth = () => {
  const d = startOfMonth();
  d.setMonth(d.getMonth() - 1);
  return d;
};

const MetricsView = ({ brokers, authToken, boardData, dashboardData, onRefresh }) => {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  // Default the metrics view to the logged-in broker so Quick Entry is
  // recorded against the correct user without a manual selection step.
  // Fall back to Emmanuel only when the auth context hasn't populated yet.
  const [selectedBroker, setSelectedBroker] = useState(() => user?.id || DEFAULT_METRICS_BROKER.id);
  const [prevBoardData, setPrevBoardData] = useState(null);
  const [quickEntry, setQuickEntry] = useState(() =>
    Object.fromEntries(MANUAL_ENTRY_METRICS.map(m => [m.key, 0]))
  );
  const [editingEntry, setEditingEntry] = useState(null);
  const [editValues, setEditValues] = useState({});

  // Once auth resolves, snap selectedBroker to the logged-in user — but only
  // if the user hasn't manually switched to a different broker. We detect
  // "hasn't switched" by checking that selectedBroker is still the initial
  // Emmanuel fallback and doesn't already match the logged-in user.
  useEffect(() => {
    if (user?.id && selectedBroker === DEFAULT_METRICS_BROKER.id && user.id !== DEFAULT_METRICS_BROKER.id) {
      setSelectedBroker(user.id);
    }
  }, [user?.id]);

  const headers = { Authorization: `Bearer ${authToken}` };

  // Resolve the broker we're recording / viewing for.
  const selectedBrokerObj = brokers.find(b => b.id === selectedBroker) ||
    (selectedBroker === DEFAULT_METRICS_BROKER.id ? DEFAULT_METRICS_BROKER : null);
  const effectiveBrokerId = selectedBroker;
  const effectiveBrokerName = selectedBrokerObj?.name || DEFAULT_METRICS_BROKER.name;

  // Brokers list with Emmanuel always first.
  const brokerOptions = [
    DEFAULT_METRICS_BROKER,
    ...brokers.filter(b => b.id !== DEFAULT_METRICS_BROKER.id),
  ];

  // --- Data loaders ───────────────────────────────────────────────────────
  const fetchEntries = useCallback(async () => {
    if (!authToken) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedBroker && selectedBroker !== 'all') params.append('user_id', selectedBroker);
      const r = await fetch(`${backendUrl}/api/gameplan/metrics?${params}&limit=50`, { headers });
      if (r.ok) {
        const d = await r.json();
        setEntries(Array.isArray(d) ? d : []);
      }
    } catch (e) {
      toast.error('Failed to load recent entries');
    } finally {
      setLoading(false);
    }
  }, [authToken, selectedBroker]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  // --- Previous month board fetch (for "vs last month" trend arrows) ──────
  // We mirror the parent's monthly board fetch, just one month earlier, so
  // the funnel can render trend deltas (↑/↓) on every conversion %.
  useEffect(() => {
    if (!authToken || !boardData?.month) {
      setPrevBoardData(null);
      return;
    }
    const [yearStr, monthStr] = boardData.month.split('-');
    const year = parseInt(yearStr, 10);
    const month = parseInt(monthStr, 10);
    const prevMonth = month === 1 ? 12 : month - 1;
    const prevYear = month === 1 ? year - 1 : year;
    const prevMonthKey = `${prevYear}-${String(prevMonth).padStart(2, '0')}`;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${backendUrl}/api/gameplan/board/${prevMonthKey}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (!r.ok) return;
        const d = await r.json();
        if (!cancelled) setPrevBoardData(d);
      } catch (e) { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [authToken, boardData?.month]);

  // --- Derived counts (per period) ────────────────────────────────────────
  // Three derived fields (Lodgments / Approvals / Settlements) come from
  // boardData.board = { [weekIdx]: { [stage]: [deals] } }. We flatten and
  // filter by stage + selected broker (if not 'all').
  const allBoardDeals = (() => {
    if (!boardData?.board) return [];
    const out = [];
    Object.values(boardData.board).forEach((stageMap) => {
      Object.values(stageMap || {}).forEach((deals) => {
        if (Array.isArray(deals)) out.push(...deals);
      });
    });
    if (selectedBroker && selectedBroker !== 'all') {
      return out.filter(d => d.broker_id === selectedBroker || d.broker_name === effectiveBrokerName);
    }
    return out;
  })();

  const countDealsInStageByPeriod = (stage, periodStart) => {
    return allBoardDeals.filter(d => {
      if (d.status !== stage) return false;
      const ts = d.updated_at || d.created_at;
      if (!periodStart) return true;
      if (!ts) return false;
      return new Date(ts) >= periodStart;
    }).length;
  };

  // Manual fields are aggregated from `entries` (POST /api/gameplan/metrics
  // history). Each entry has a `date` field plus the metric columns.
  const sumEntriesByPeriod = (metricKey, periodStart) => {
    return entries.reduce((acc, e) => {
      if (periodStart && (!e.date || new Date(e.date) < periodStart)) return acc;
      return acc + (e[metricKey] || 0);
    }, 0);
  };

  const derivedFor = (period) => {
    const ranges = {
      today: startOfToday(),
      week:  startOfWeek(),
      month: startOfMonth(),
      all:   null,
    };
    const cutoff = ranges[period];
    return {
      portal_invites:     sumEntriesByPeriod('portal_invites', cutoff),
      portal_completions: sumEntriesByPeriod('portal_completions', cutoff),
      proposals:          sumEntriesByPeriod('proposals', cutoff),
      lodgments:   countDealsInStageByPeriod('submitted', cutoff),
      approvals:   countDealsInStageByPeriod('approved', cutoff),
      settlements: countDealsInStageByPeriod('settled', cutoff),
    };
  };

  const todayCounts = derivedFor('today');
  const weekCounts  = derivedFor('week');
  const monthCounts = derivedFor('month');

  // --- Previous month counts (for "vs last month" trend in funnel) ────────
  // Mirror the same derivation against last month's board + entries dated
  // between [startOfPrevMonth, startOfMonth).
  const allPrevBoardDeals = (() => {
    if (!prevBoardData?.board) return [];
    const out = [];
    Object.values(prevBoardData.board).forEach((stageMap) => {
      Object.values(stageMap || {}).forEach((deals) => {
        if (Array.isArray(deals)) out.push(...deals);
      });
    });
    if (selectedBroker && selectedBroker !== 'all') {
      return out.filter(d => d.broker_id === selectedBroker || d.broker_name === effectiveBrokerName);
    }
    return out;
  })();

  const sumEntriesInPrevMonth = (metricKey) => {
    const start = startOfPrevMonth();
    const end = startOfMonth();
    return entries.reduce((acc, e) => {
      if (!e.date) return acc;
      const t = new Date(e.date);
      if (t < start || t >= end) return acc;
      return acc + (e[metricKey] || 0);
    }, 0);
  };

  const countPrevDealsInStage = (stage) =>
    allPrevBoardDeals.filter(d => d.status === stage).length;

  const prevMonthCounts = {
    portal_invites:     sumEntriesInPrevMonth('portal_invites'),
    portal_completions: sumEntriesInPrevMonth('portal_completions'),
    proposals:          sumEntriesInPrevMonth('proposals'),
    lodgments:   countPrevDealsInStage('submitted'),
    approvals:   countPrevDealsInStage('approved'),
    settlements: countPrevDealsInStage('settled'),
  };

  // --- Quick entry handlers ───────────────────────────────────────────────
  const handleQuickAdd = (key, delta) => {
    setQuickEntry(prev => ({ ...prev, [key]: Math.max(0, (prev[key] || 0) + delta) }));
  };

  const handleSubmitQuickEntry = async () => {
    const anySet = Object.values(quickEntry).some(v => v > 0);
    if (!anySet) {
      toast.error('Add at least one value first');
      return;
    }
    if (!effectiveBrokerId) {
      toast.error('User not identified');
      return;
    }

    const gameplanPayload = {
      user_id: effectiveBrokerId,
      user_name: effectiveBrokerName || 'Unknown',
      portal_invites:     quickEntry.portal_invites,
      portal_completions: quickEntry.portal_completions,
      proposals:          quickEntry.proposals,
      // Lodgments, Approvals and Settlements are intentionally NOT included
      // here — they're derived from the Board / Lodged Deals / Dashboard tabs
      // so there's a single source of truth and the broker never has to
      // double-key those numbers.
    };

    try {
      await fetch(`${backendUrl}/api/gameplan/metrics`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(gameplanPayload),
      });

      toast.success('Logged for today');
      setQuickEntry(Object.fromEntries(MANUAL_ENTRY_METRICS.map(m => [m.key, 0])));
      fetchEntries();
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error('Failed to save — please retry');
    }
  };

  const handleStartEdit = (entry) => {
    setEditingEntry(entry.id);
    setEditValues({
      portal_invites:     entry.portal_invites     || 0,
      portal_completions: entry.portal_completions || 0,
      proposals:          entry.proposals          || 0,
      lodgments:          entry.lodgments          || 0,
      approvals:          entry.approvals          || 0,
      settlements:        entry.settlements        || 0,
    });
  };
  const handleSaveEdit = async (entryId) => {
    try {
      await fetch(`${backendUrl}/api/gameplan/metrics/${entryId}`, {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(editValues),
      });
      toast.success('Entry updated');
      setEditingEntry(null);
      fetchEntries();
      if (onRefresh) onRefresh();  // Cascade to Business Dashboard so KPIs recompute
    } catch (e) { toast.error('Failed to update'); }
  };
  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await fetch(`${backendUrl}/api/gameplan/metrics/${entryId}`, { method: 'DELETE', headers });
      toast.success('Deleted');
      fetchEntries();
      if (onRefresh) onRefresh();  // Cascade to Business Dashboard so KPIs recompute
    } catch (e) { toast.error('Failed to delete'); }
  };

  // Loading screen only while initial entries fetch is happening
  if (loading && entries.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const pipelineMetrics = METRICS;

  return (
    <div className="space-y-6" data-testid="metrics-view">
      {/* ─── 1. Quick Entry — Today (at the very top) ───────────────────── */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-4 sm:p-6 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
          <div className="flex items-center gap-3 flex-wrap">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
              <Plus className="w-5 h-5 text-emerald-600" />
              Quick Entry — Today
            </h3>
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {new Date().toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'short', timeZone: 'Australia/Hobart' })}
            </span>
            <div className="flex items-center gap-2 ml-auto md:ml-2 bg-slate-50 dark:bg-slate-800 px-3 py-1.5 rounded-full border border-slate-200 dark:border-slate-700">
              <Users className="w-3.5 h-3.5 text-emerald-600" />
              <span className="text-[11px] uppercase tracking-widest font-bold text-slate-600 dark:text-slate-300">
                Recording as
              </span>
              <Select value={selectedBroker} onValueChange={setSelectedBroker}>
                <SelectTrigger className="h-7 w-[160px] border-0 bg-transparent text-sm font-bold px-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {brokerOptions.map(b => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.name}{b.id === DEFAULT_METRICS_BROKER.id ? ' ⭐' : ''}
                    </SelectItem>
                  ))}
                  <SelectItem value="all">All brokers (view only)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button
            onClick={handleSubmitQuickEntry}
            className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
            data-testid="submit-metrics-btn"
            disabled={selectedBroker === 'all'}
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            Record for {effectiveBrokerName}
          </Button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
          {MANUAL_ENTRY_METRICS.map(m => {
            const Icon = m.icon;
            return (
              <div
                key={m.key}
                className="bg-slate-50 dark:bg-slate-800/70 rounded-lg p-3 text-center border border-slate-200 dark:border-slate-700"
                data-testid={`quick-entry-${m.key}`}
              >
                <div className="flex items-center justify-center gap-1 mb-1.5">
                  <Icon className="w-3 h-3 text-slate-500 dark:text-slate-400" />
                  <p className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">{m.short}</p>
                </div>
                <div className="flex items-center justify-center gap-1.5">
                  <button
                    onClick={() => handleQuickAdd(m.key, -1)}
                    className="w-7 h-7 rounded-full bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 flex items-center justify-center transition-colors"
                    data-testid={`${m.key}-minus`}
                    aria-label={`Decrease ${m.label}`}
                  >
                    <Minus className="w-3.5 h-3.5" />
                  </button>
                  <span className="text-xl font-bold text-slate-900 dark:text-white tabular-nums w-7 text-center">
                    {quickEntry[m.key]}
                  </span>
                  <button
                    onClick={() => handleQuickAdd(m.key, 1)}
                    className="w-7 h-7 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white flex items-center justify-center transition-colors"
                    data-testid={`${m.key}-plus`}
                    aria-label={`Increase ${m.label}`}
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Hint about auto-derived metrics — answers "where are Lodgments / Approvals / Settlements?" */}
        <div className="mt-3 flex items-start gap-2 text-[11px] text-slate-500 dark:text-slate-400">
          <span className="font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500 shrink-0">Auto-tracked</span>
          <span>
            Lodgments, Approvals & Settlements update live from your <strong>Board</strong>,
            <strong> Lodged Deals</strong> and <strong>Dashboard</strong> tabs — no need to key them here.
          </span>
        </div>
      </div>

      {/* ─── 2. Live counts — Today / Week / Month / All-Time ───────────── */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <h3 className="text-sm font-bold uppercase tracking-widest text-slate-600 dark:text-slate-400">
            Live counts — derived from your Board, Lodged Deals, Dashboard & Portal data
          </h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <PeriodCard label="Today"     icon={CalendarDays} gradient="from-blue-500 to-blue-600"       counts={todayCounts} testId="period-today" />
          <PeriodCard label="This Week" icon={Calendar}     gradient="from-purple-500 to-purple-600"   counts={weekCounts}  testId="period-week"  />
          <PeriodCard label="This Month" icon={TrendingUp}  gradient="from-emerald-500 to-emerald-600" counts={monthCounts} testId="period-month" />
        </div>
      </div>

      {/* ─── 3. Conversion Funnel ───────────────────────────────────────── */}
      <ConversionFunnel monthCounts={monthCounts} prevMonthCounts={prevMonthCounts} metrics={pipelineMetrics} />

      {/* ─── 3b. Coach's Note — picks the weakest stage and writes a one-line action prompt ─── */}
      <CoachNote monthCounts={monthCounts} prevMonthCounts={prevMonthCounts} metrics={pipelineMetrics} brokerName={effectiveBrokerName} />

      {/* ─── 4. Recent Entries (manual gameplan-metric logs) ─────────────── */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden shadow-sm">
        <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
            <Clock className="w-5 h-5 text-slate-600" />
            Recent Entries
          </h3>
          <Button variant="ghost" size="sm" onClick={fetchEntries}>
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300">Broker</th>
                {pipelineMetrics.map(m => (
                  <th key={m.key} className="px-3 py-3 text-center text-xs font-semibold text-slate-600 dark:text-slate-300">{m.short}</th>
                ))}
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 dark:text-slate-300">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {entries.length === 0 ? (
                <tr>
                  <td colSpan={pipelineMetrics.length + 3} className="px-4 py-8 text-center text-slate-500">
                    No manual entries yet — use Quick Entry above to log activity.
                  </td>
                </tr>
              ) : (
                entries.map(entry => (
                  <tr key={entry.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/30">
                    <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">
                      {new Date(entry.date).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })}
                    </td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{entry.user_name}</td>
                    {editingEntry === entry.id ? (
                      <>
                        {pipelineMetrics.map(m => (
                          <td key={m.key} className="px-2 py-2 text-center">
                            <Input
                              type="number"
                              min="0"
                              value={editValues[m.key]}
                              onChange={(e) => setEditValues(prev => ({ ...prev, [m.key]: parseInt(e.target.value) || 0 }))}
                              className="w-16 h-8 text-center text-sm"
                            />
                          </td>
                        ))}
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button size="sm" variant="ghost" onClick={() => handleSaveEdit(entry.id)} className="h-8 w-8 p-0 text-emerald-600">
                              <Save className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditingEntry(null)} className="h-8 w-8 p-0 text-slate-500">
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        {pipelineMetrics.map(m => (
                          <td key={m.key} className="px-3 py-3 text-center">
                            <span className={cn(
                              'inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold tabular-nums',
                              (entry[m.key] || 0) > 0
                                ? 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white'
                                : 'text-slate-300 dark:text-slate-600'
                            )}>
                              {entry[m.key] || 0}
                            </span>
                          </td>
                        ))}
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button size="sm" variant="ghost" onClick={() => handleStartEdit(entry)} className="h-8 w-8 p-0 text-slate-500">
                              <Save className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleDeleteEntry(entry.id)} className="h-8 w-8 p-0 text-rose-500">
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ─── ConversionFunnel ──────────────────────────────────────────────────────
// A true conversion funnel: each stage's count is rendered as a horizontal
// trapezoid whose width is proportional to that stage vs the top (Invites).
// Between consecutive stages we surface the conversion % (current / previous)
// with colour-coded health (green ≥70%, amber 40-69%, red <40%, grey when
// the previous stage is 0 so the rate is undefined).
//
// The "overall pipeline conversion" pill at the top is Settlements / Invites —
// the lifetime % of the broker's portal funnel that turns into a settled deal.
const ConversionFunnel = ({ monthCounts, prevMonthCounts, metrics }) => {
  const stages = metrics.map(m => ({
    ...m,
    count: monthCounts[m.key] || 0,
    prevCount: prevMonthCounts?.[m.key] || 0,
  }));
  const topCount = Math.max(stages[0]?.count || 0, 1);
  const overallPct = stages[0]?.count > 0
    ? Math.round(((stages[stages.length - 1]?.count || 0) / stages[0].count) * 100)
    : null;
  const prevOverallPct = stages[0]?.prevCount > 0
    ? Math.round(((stages[stages.length - 1]?.prevCount || 0) / stages[0].prevCount) * 100)
    : null;
  const overallDelta = (overallPct !== null && prevOverallPct !== null) ? overallPct - prevOverallPct : null;

  const conversionFor = (idx) => {
    if (idx === 0) return null;
    const prev = stages[idx - 1].count;
    const curr = stages[idx].count;
    if (prev === 0) return { pct: null, dropped: 0, delta: null };

    const pct = Math.round((curr / prev) * 100);
    const prevPrev = stages[idx - 1].prevCount;
    const prevCurr = stages[idx].prevCount;
    const prevPct = prevPrev > 0 ? Math.round((prevCurr / prevPrev) * 100) : null;
    const delta = prevPct !== null ? pct - prevPct : null;

    return { pct, dropped: prev - curr, delta };
  };

  const healthClass = (pct) => {
    if (pct === null) return 'bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500';
    if (pct >= 70)    return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300';
    if (pct >= 40)    return 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300';
    return 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300';
  };

  // Tiny ↑/↓ chip — green when up, rose when down, slate when flat. Shown
  // beside every conversion pill that has a comparable prior-month value.
  const DeltaChip = ({ delta }) => {
    if (delta === null || delta === undefined) return null;
    if (delta === 0) {
      return (
        <span
          className="inline-flex items-center gap-0.5 text-[10px] font-bold text-slate-500 dark:text-slate-400"
          title="No change vs last month"
        >
          <MinusIcon className="w-3 h-3" /> 0%
        </span>
      );
    }
    const up = delta > 0;
    return (
      <span
        className={cn(
          'inline-flex items-center gap-0.5 text-[10px] font-bold',
          up ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400',
        )}
        title={`${up ? 'Up' : 'Down'} ${Math.abs(delta)}% vs last month`}
      >
        {up ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
        {Math.abs(delta)}%
      </span>
    );
  };

  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm" data-testid="conversion-funnel">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-5">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-slate-600" />
          Monthly Conversion Funnel
        </h3>
        {overallPct !== null && (
          <div
            className={cn(
              'inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest self-start sm:self-auto',
              healthClass(overallPct),
            )}
            data-testid="funnel-overall-conversion"
          >
            <TrendingUp className="w-3.5 h-3.5" />
            Overall Invite → Settle: {overallPct}%
            <DeltaChip delta={overallDelta} />
          </div>
        )}
      </div>

      <div className="space-y-1">
        {stages.map((s, idx) => {
          const widthPct = Math.max((s.count / topCount) * 100, 6); // floor so empty stages still render
          const conv = conversionFor(idx);
          return (
            <React.Fragment key={s.key}>
              {/* Stage row */}
              <div className="flex items-center gap-3" data-testid={`funnel-stage-${s.key}`}>
                <div className="w-28 sm:w-36 shrink-0 text-right">
                  <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400 leading-tight">
                    {s.label}
                  </p>
                  {!s.manual && (
                    <p className="text-[9px] uppercase tracking-widest text-slate-400 dark:text-slate-500 leading-tight">
                      from Board
                    </p>
                  )}
                </div>
                <div className="flex-1 min-w-0 flex items-center">
                  <div
                    className={cn(
                      'h-10 sm:h-12 rounded-r-lg transition-all flex items-center justify-end pr-3 text-white font-bold tabular-nums shadow-sm',
                      s.color,
                    )}
                    style={{ width: `${widthPct}%`, minWidth: '48px' }}
                  >
                    {s.count}
                  </div>
                </div>
              </div>

              {/* Conversion arrow + % between stages */}
              {idx < stages.length - 1 && (
                <div className="flex items-center gap-3" data-testid={`funnel-conversion-${s.key}-to-${stages[idx + 1].key}`}>
                  <div className="w-28 sm:w-36 shrink-0" aria-hidden="true" />
                  <div className="flex-1 flex items-center gap-2 py-1 pl-1">
                    <ArrowDown className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500 shrink-0" />
                    {conversionFor(idx + 1)?.pct === null ? (
                      <span className="text-[10px] uppercase tracking-widest font-bold text-slate-400 dark:text-slate-500">
                        No data yet
                      </span>
                    ) : (
                      <>
                        <span
                          className={cn(
                            'text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full',
                            healthClass(conversionFor(idx + 1).pct),
                          )}
                        >
                          {conversionFor(idx + 1).pct}% conversion
                        </span>
                        <DeltaChip delta={conversionFor(idx + 1).delta} />
                        {conversionFor(idx + 1).dropped > 0 && (
                          <span className="inline-flex items-center gap-1 text-[10px] text-slate-500 dark:text-slate-400">
                            <TrendingDown className="w-3 h-3 text-rose-400" />
                            {conversionFor(idx + 1).dropped} dropped
                          </span>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-5 pt-4 border-t border-slate-200 dark:border-slate-700 flex flex-wrap items-center gap-3 text-[10px] uppercase tracking-widest font-bold text-slate-500 dark:text-slate-400">
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-500"></span> ≥ 70% — healthy
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-2.5 rounded-full bg-amber-500"></span> 40–69% — watch
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-2.5 rounded-full bg-rose-500"></span> &lt; 40% — fix
        </span>
        <span className="ml-auto inline-flex items-center gap-1 normal-case tracking-normal text-slate-400">
          <ArrowUp className="w-3 h-3 text-emerald-500" />
          <ArrowDown className="w-3 h-3 text-rose-500" />
          = vs last month
        </span>
      </div>
    </div>
  );
};

// ─── CoachNote ─────────────────────────────────────────────────────────────
// Looks at the funnel + delta data and picks a single highest-impact stage
// to nudge the broker about. Priority:
//   1. Biggest negative delta vs last month (something is actively declining)
//   2. Worst conversion stage that has data (lowest %, must be < 70%)
//   3. Healthy funnel → positive reinforcement
//   4. No data at all → onboarding prompt
//
// Runs purely on the client — no LLM call, deterministic, instant.
const STAGE_COACHING = {
  // key = "from→to" using metric.key pairs
  'portal_invites→portal_completions': {
    weak: "Most portal drop-offs happen in the first 48 hours after invite. Set a follow-up call or SMS reminder within 24 hrs of sending the invite.",
    declining: "Portal completions dropped — review your invite message and send a personal SMS nudge before day 2.",
  },
  'portal_completions→proposals': {
    weak: "Clients who finish the portal are warmest. Book the proposal call within 24 hrs of completion before the urgency fades.",
    declining: "Fewer completions are turning into proposals — try a same-day call right when the portal status flips to complete.",
  },
  'proposals→lodgments': {
    weak: "Proposals aren't lodging fast enough. Confirm lender choice + doc checklist in the proposal call so you can submit within 48 hrs.",
    declining: "Lodgement rate is slipping — chase outstanding docs proactively and pre-fill applications in your CRM before the meeting.",
  },
  'lodgments→approvals': {
    weak: "Lodgements aren't getting approved cleanly. Pre-check serviceability + LVR before lodging and call your BDM if scenarios are borderline.",
    declining: "Approval rate is dropping — review recent declines for patterns (income, credit, LVR) and adjust scenarios early.",
  },
  'approvals→settlements': {
    weak: "Approvals aren't crossing the line. Set a weekly check-in with solicitor + lender during the post-approval window to keep the file moving.",
    declining: "Settlements are slowing — escalate stuck files; offer to introduce the client to your solicitor partner to remove friction.",
  },
};

const CoachNote = ({ monthCounts, prevMonthCounts, metrics, brokerName }) => {
  const stages = metrics.map(m => ({
    ...m,
    count: monthCounts[m.key] || 0,
    prevCount: prevMonthCounts?.[m.key] || 0,
  }));

  // No data yet → onboarding nudge
  const totalThisMonth = stages.reduce((s, x) => s + x.count, 0);
  if (totalThisMonth === 0) {
    return (
      <CoachShell tone="neutral" icon={Lightbulb}>
        <p>
          <strong>Get started:</strong> log your first Portal Invite in Quick Entry above. The funnel
          will start telling you exactly which stage to focus on next.
        </p>
      </CoachShell>
    );
  }

  // Build per-transition conversion rates + deltas
  const transitions = [];
  for (let i = 1; i < stages.length; i++) {
    const from = stages[i - 1];
    const to = stages[i];
    const pct = from.count > 0 ? Math.round((to.count / from.count) * 100) : null;
    const prevPct = from.prevCount > 0 ? Math.round((to.prevCount / from.prevCount) * 100) : null;
    const delta = (pct !== null && prevPct !== null) ? pct - prevPct : null;
    transitions.push({ fromKey: from.key, toKey: to.key, fromLabel: from.label, toLabel: to.label, pct, prevPct, delta });
  }

  // 1) Biggest decline vs last month (only if at least 5pp drop)
  const declines = transitions.filter(t => t.delta !== null && t.delta <= -5);
  declines.sort((a, b) => a.delta - b.delta); // most negative first
  if (declines.length > 0) {
    const t = declines[0];
    const messageKey = `${t.fromKey}→${t.toKey}`;
    const msg = STAGE_COACHING[messageKey]?.declining || `Your ${t.fromLabel} → ${t.toLabel} conversion dropped ${Math.abs(t.delta)}% — investigate this stage first.`;
    return (
      <CoachShell tone="warn" icon={Sparkles}>
        <p>
          <strong>{brokerName?.split(' ')[0] || 'Hey'},</strong> your{' '}
          <strong>{t.fromLabel} → {t.toLabel}</strong> conversion dropped{' '}
          <strong className="text-rose-600 dark:text-rose-300">{Math.abs(t.delta)}%</strong>{' '}
          vs last month ({t.prevPct}% → {t.pct}%). {msg}
        </p>
      </CoachShell>
    );
  }

  // 2) Worst conversion stage that has data and is < 70%
  const weak = transitions.filter(t => t.pct !== null && t.pct < 70);
  weak.sort((a, b) => a.pct - b.pct);
  if (weak.length > 0) {
    const t = weak[0];
    const messageKey = `${t.fromKey}→${t.toKey}`;
    const msg = STAGE_COACHING[messageKey]?.weak || `Lift this stage and the rest of the funnel benefits.`;
    return (
      <CoachShell tone={t.pct < 40 ? 'critical' : 'watch'} icon={Sparkles}>
        <p>
          <strong>Biggest leak:</strong> only{' '}
          <strong>{t.pct}%</strong> of your <strong>{t.fromLabel}</strong> turn into{' '}
          <strong>{t.toLabel}</strong>. {msg}
        </p>
      </CoachShell>
    );
  }

  // 3) Everything ≥ 70% → positive reinforcement
  return (
    <CoachShell tone="success" icon={Sparkles}>
      <p>
        <strong>Strong funnel this month{brokerName ? `, ${brokerName.split(' ')[0]}` : ''}.</strong>{' '}
        Every transition is above 70%. Focus on growing the top of the funnel — more Portal Invites
        means more settlements at the same conversion rate.
      </p>
    </CoachShell>
  );
};

const CoachShell = ({ tone, icon: Icon, children }) => {
  const tones = {
    success:  'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-100',
    warn:     'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100',
    watch:    'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100',
    critical: 'border-rose-200 bg-rose-50 text-rose-900 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-100',
    neutral:  'border-slate-200 bg-slate-50 text-slate-800 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-200',
  };
  const iconTones = {
    success:  'text-emerald-600 dark:text-emerald-400',
    warn:     'text-amber-600 dark:text-amber-400',
    watch:    'text-amber-600 dark:text-amber-400',
    critical: 'text-rose-600 dark:text-rose-400',
    neutral:  'text-slate-500 dark:text-slate-400',
  };
  return (
    <div
      className={cn('rounded-xl border p-4 shadow-sm flex items-start gap-3', tones[tone])}
      data-testid={`coach-note-${tone}`}
    >
      <div className="shrink-0 mt-0.5">
        <Icon className={cn('w-5 h-5', iconTones[tone])} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest font-bold mb-1 opacity-70">
          Coach's note
        </div>
        <div className="text-sm leading-relaxed">
          {children}
        </div>
      </div>
    </div>
  );
};

// Period summary card — 6 mini-stats in a 3x2 grid, derived from real data.
const PeriodCard = ({ label, icon: Icon, gradient, counts, testId }) => (
  <div
    className={cn('rounded-xl p-5 text-white bg-gradient-to-br', gradient)}
    data-testid={testId}
  >
    <div className="flex items-center justify-between mb-3">
      <h4 className="font-semibold text-base">{label}</h4>
      <Icon className="w-5 h-5 opacity-80" />
    </div>
    <div className="grid grid-cols-3 gap-2 text-center">
      {METRICS.map(m => (
        <div key={m.key}>
          <p className="text-xl font-bold tabular-nums">{counts[m.key] || 0}</p>
          <p className="text-[9px] opacity-80 uppercase tracking-wider leading-tight">{m.short}</p>
        </div>
      ))}
    </div>
  </div>
);

export { MetricsView };
export default MetricsView;
